// src/components/BillHistory.tsx
//
// The approval-lifecycle ledger for one bill, read from
// `GET /api/bills/:reference/transitions`.
//
// ⚠ A HISTORY, NOT A TABLE, and that is a decision about what the data is. It
// is a short append-only sequence, in the order things happened, and the database
// makes it immutable (009).
//
// ⚠ **"THREE ENTRIES AT MOST" — WHICH THIS COMMENT USED TO CLAIM — IS NO LONGER
// TRUE, and the correction matters because a bound nobody re-checked is how a
// screen starts lying.** Wave 10's pause and referral (060, Annex A 37/38) can
// repeat: a bill can be paused, resumed, paused again and referred twice, so a
// ledger of a dozen entries is now ordinary rather than a finding. The gateway
// caps the list at `MAX_TRANSITIONS_PER_BILL` and says when it did — which is what
// the `truncated` banner below is for, and why that banner's own wording ("a bill
// with a ledger this long is itself a finding") is now the only place a length
// judgement is made. A table invites the
// reader to sort and compare columns; a sequence says "this happened, then
// this", which is what an audit trail is for and what a compliance reader is
// looking for. `<ol>` rather than `<ul>` for the same reason: the order carries
// meaning, and a screen reader announces it as numbered.
//
// ⚠ This is the ONLY surface for two facts. `app.bill.approved_by` keeps the
// LEVEL 1 approver even after level 2 approval, so the officer who gave final
// approval appears nowhere else; and a rejection's reason — required by the API,
// written to this ledger — was captured and unreadable until this existed.
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { UNRECORDED_ACTOR_ROLE } from '@prs2/contracts'
import { billStatusLabel, formatInstant } from '../lib/format'
import {
  hasReason,
  isReasonMissing,
  referralTargetName,
  resumeDestinationSentence,
  transitionEffect,
  transitionHeadline,
  transitionTone,
} from '../lib/transitions'
import type { BillTransition } from '../lib/types'

export function BillHistory({
  billStatus,
  transitions,
  truncated = false,
  loading,
  error,
  onRetry,
}: {
  /** The bill's own status — needed to tell an empty ledger from a MISSING one. */
  billStatus: string
  transitions: readonly BillTransition[] | undefined
  /**
   * The gateway capped this list. These entries read OLDEST FIRST, so the
   * missing ones are the NEWEST — the most recent decision, which is the one
   * the reader almost certainly came for. Never rendered silently.
   */
  truncated?: boolean
  loading: boolean
  error: string | null
  onRetry: () => void
}) {
  // ⚠ An empty ledger means two completely different things, and saying the
  // wrong one puts a false statement next to the bill's own status badge.
  //
  // Caught in the browser, not in review: decided bills on the local database
  // have NO ledger entry at all — they carry `status = 'APPROVED'` written
  // straight onto the bill row, before `app.bill_transition` existed to record
  // how they got there. The first draft of this card told the reader "Nobody has
  // approved or rejected this bill" directly underneath a badge reading
  // **Approved**.
  //
  // So: an undecided bill with no entries has simply not been decided. A DECIDED
  // bill with no entries is a gap in the record, and the card has to say that
  // rather than deny the decision the bill itself records. Both branches are
  // asserted on the rendered DOM in `BillDetailPage.dom.test.tsx`.
  //
  // ══ RE-MEASURED 2026-08-13, AND THE COUNT WAS THE LESS INTERESTING HALF ══
  //
  // The figure recorded here was "12 of the 17 decided bills". Against the live
  // database it is now **14 of 28**, and the composition matters more than the
  // count:
  //
  //   ·  8  are seed rows (`BILL-*`, `005_seed_synthetic.sql`) — the demo corpus
  //   ·  6  are TEST FIXTURES left behind in the shared dev database
  //         (`CMP-B-ALREADY-APPROVED`, `CMP-B-REJECTED-BARE`,
  //          `CMP-B-TRAPPED-APPROVED`, `CMP-B-TRAPPED-AUTH`,
  //          `LFC-B-ORDER-DECIDED`, `TEST-B-RP-RECOVER`)
  //
  // And every one of the 14 also has `approved_by IS NULL` and
  // `approved_at IS NULL`. The gap is not "the ledger is missing an entry" — the
  // decision is unrecorded EVERYWHERE, which is exactly what this card says.
  //
  // ══ BACKFILL, OR ACCEPT? — accept for existing data; fix the SEED forward ══
  //
  // **Do not backfill `app.bill_transition` on any existing database.** Inventing
  // an approver, a timestamp and a level for a decision nobody made is
  // fabricating an audit record, on the one table whose entire value is that it
  // records what actually happened — and 009 makes it append-only, so a wrong
  // backfill is permanent.
  //
  // **Do change the seed, in a NEW migration, for fresh installs.** Its eight
  // decided bills are currently incoherent as synthetic data: APPROVED, by
  // nobody, at no time, with no ledger row — so a demo of the approval ledger
  // lands on the empty state for eight of the forty bills. A seed that fabricates
  // a bill, an officer and an amount can fabricate the decision that matches
  // them; what it must not do is leave three columns and a table disagreeing. A
  // new migration (not an edit to 005 — `ON CONFLICT DO NOTHING` makes that a
  // no-op on every existing database while looking like a fix) should write
  // `approved_by`/`approved_at`/`approved_level` and the matching ledger row
  // together, guarded so it never touches a bill decided through the product.
  //
  // ⚠ That change lives in `infra/local/sql/`, which this task does not own — it
  // is reported, not made. THIS branch stays either way: it is the correct
  // statement for any bill that predates the table, and for test residue that no
  // seed change can reach.
  const decided = billStatus === 'APPROVED' || billStatus === 'REJECTED'
  return (
    <div className="pt-card">
      <h2 className="text-lg mb-1">Decision history</h2>
      <p className="text-sm text-[var(--sgds-color-subtle)] mb-4 max-w-[70ch]">
        The append-only ledger of every lifecycle transition on this bill: approvals, rejections, pauses
        for information, resumes and referrals. It is the only record of who approved at level 2, of why a
        bill was rejected, and of which queue a paused bill will return to. Entries are never edited or
        removed; the database enforces that.
      </p>

      {error !== null ? (
        <div role="alert" className="pt-well p-4">
          <p className="font-semibold text-[var(--sgds-danger-color-emphasis)]">Could not load the decision history</p>
          <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">{error}</p>
          <p className="mt-2 text-sm text-[var(--sgds-color-subtle)]">
            This is the history failing to load: it is not a statement that the bill has no history. Nothing
            below should be read as a complete record until it loads.
          </p>
          <div className="mt-4">
            <SgdsButton variant="outline" type="button" onClick={onRetry}>
              Try again
            </SgdsButton>
          </div>
        </div>
      ) : loading && transitions === undefined ? (
        <p className="text-sm text-[var(--sgds-body-color-subtle)]" aria-busy="true">
          Loading the decision history…
        </p>
      ) : (transitions?.length ?? 0) === 0 ? (
        <div className="pt-well p-4">
          <p className="font-semibold">
            {decided ? 'This bill was decided before the ledger recorded decisions' : 'No decision recorded yet'}
          </p>
          <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
            {decided
              ? `The bill is ${billStatusLabel(billStatus).toLowerCase()}, and the ledger holds no entry explaining how it got there, so who decided it, when, and on what reason cannot be answered from this screen. That is a gap in the record, not an absence of decision: the transition ledger did not exist when this bill was decided. Every decision taken through this product from now on writes an entry here.`
              : 'Nobody has approved or rejected this bill. That is the whole record for this bill.'}
          </p>
        </div>
      ) : (
        <>
        {truncated && (
          <div role="alert" className="pt-well p-4 mb-3">
            <p className="font-semibold text-[var(--sgds-danger-color-emphasis)]">This history is incomplete</p>
            <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
              This bill has more transitions than this screen will load, and the entries below are the
              <strong> oldest</strong> ones, so the most recent decision is <strong>not</strong> shown. Do not
              read the last entry below as the current state of the bill; the status badge at the top of the page
              is authoritative. Report the reference to the service desk: a bill with a ledger this long is
              itself a finding.
            </p>
          </div>
        )}
        <ol className="pt-history" aria-busy={loading}>
          {transitions?.map((entry) => (
            <li key={entry.id} className={`pt-history-item pt-history-item--${transitionTone(entry)}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <p className="font-semibold">{transitionHeadline(entry)}</p>
                <p className="text-sm pt-tabular text-[var(--sgds-color-subtle)]">{formatInstant(entry.occurredAt)}</p>
              </div>

              {/*
                ⚠ `actorDisplayName` is NULLABLE, and `null` means WITHHELD —
                never unknown. The gateway does not send the deciding officer's
                name or id to a role with no need for it (P2C11 §3.13; see
                `mayReadApproverIdentity`). Saying so is better than rendering
                an empty "by" or, worse, the word "null": an external provider
                should understand that a real officer decided this and that
                which officer is not their business, not be left wondering
                whether the record is broken.

                ★ 2026-08-16 — the FIRST test is now `actorIsSystem`, not
                `actorRole === null`. `008a` made the ledger record a role, and a
                service actor carries `'PRS_SERVICE'`, so every new saga reversal
                began falling through to the withheld branch below and claiming
                an officer was being hidden from this reader. Nobody is hidden;
                nobody decided it. `actorRole === null` is kept as the second
                test so a row written before `008a` still reads correctly.

                ⚠ I4 (final review) — `actorRole === null` is a DIFFERENT case
                from withheld: a `REVERSED` row (the saga's system
                compensating action) may have no human actor at all, and
                `mayReadApproverIdentity`'s withheld-role text ("not disclosed
                to your role") would be a FALSE statement about this row —
                nobody is being hidden, because nobody decided it.
              */}
              <p className="text-sm mt-1">
                {entry.actorIsSystem === true || entry.actorRole === null ? (
                  <span className="text-[var(--sgds-body-color-subtle)]">
                    by the system: no human officer decided this
                  </span>
                ) : entry.actorRole === UNRECORDED_ACTOR_ROLE ? (
                  /*
                    ★ A THIRD case, and it must not render as a role name.
                    `008a_bill_transition_actor_role.sql` made the ledger record
                    the actor's role AS AT the decision; a row written before it
                    has an actor and no recorded role, and the gateway sends
                    `UNRECORDED` rather than guessing. Printing that token as
                    though it were a role — "by UNRECORDED" — would read as a
                    defect; saying what it means is the honest render, and it is
                    also the true statement: nobody withheld anything and nobody
                    is unknown, the ledger simply did not capture this field
                    then.
                  */
                  <>
                    {entry.actorDisplayName !== null ? (
                      <>
                        by <strong className="font-semibold">{entry.actorDisplayName}</strong>{' '}
                      </>
                    ) : (
                      <>by an officer </>
                    )}
                    <span className="text-[var(--sgds-body-color-subtle)]">
                      (their role was not recorded on this decision: it predates the ledger keeping one)
                    </span>
                  </>
                ) : entry.actorDisplayName !== null ? (
                  <>
                    by <strong className="font-semibold">{entry.actorDisplayName}</strong>{' '}
                    <span className="text-[var(--sgds-body-color-subtle)]">({entry.actorRole})</span>
                  </>
                ) : (
                  <>
                    by <strong className="font-semibold">{entry.actorRole}</strong>{' '}
                    <span className="text-[var(--sgds-body-color-subtle)]">
                      (the individual officer is not disclosed to your role)
                    </span>
                  </>
                )}
              </p>

              <p className="text-sm mt-1 text-[var(--sgds-body-color-subtle)]">{transitionEffect(entry)}</p>

              {/*
                ★★★ WAVE 10 — WHERE A PAUSE COMES BACK TO, on the pause entry itself.

                ⚠ This is the sentence an officer needs BEFORE they write to the HSP,
                not after: a bill paused out of the level 2 queue comes back still
                needing the senior approval, and one paused out of level 1 does not.
                Without it the history says a bill was paused and leaves the reader
                to guess what resuming it will do — and the guess a reader makes is
                "back to the start", which is wrong for every bill that had already
                cleared level 1.

                ⚠ Rendered only when the ledger actually carries an address.
                `resumeDestinationSentence` returns null otherwise and this renders
                nothing rather than naming a queue the record does not.
              */}
              {resumeDestinationSentence(entry) !== null && (
                <p className="text-sm mt-1">{resumeDestinationSentence(entry)}</p>
              )}

              {/*
                ★★ WAVE 10 — WHO a referral named.

                ⚠ A `REFER` entry with no name renders no line at all, and the two
                reasons for that are deliberately indistinguishable here: either this
                is not a referral, or the individual is withheld from this reader's
                role (the gateway redacts the target with the same gate as the
                deciding officer, because a referral names a person just as surely as
                a decision does). Saying "a colleague, not disclosed to your role"
                would tell that reader there IS somebody to see — the same disclosure
                one step removed.
              */}
              {referralTargetName(entry) !== null && (
                <p className="text-sm mt-1">
                  to <strong className="font-semibold">{referralTargetName(entry)}</strong>{' '}
                  <span className="text-[var(--sgds-body-color-subtle)]">
                    (asked to look at it: the bill did not move)
                  </span>
                </p>
              )}

              {hasReason(entry) && (
                <figure className="pt-well p-3 mt-3 m-0">
                  {/* The officer's own words, quoted rather than paraphrased —
                      this is the sentence the provider is given, so the ledger
                      must show it verbatim. */}
                  <blockquote className="text-sm m-0">{entry.reason}</blockquote>
                  <figcaption className="text-xs mt-2 text-[var(--sgds-color-subtle)]">
                    Reason given, as recorded: this is what the healthcare service provider is told.
                  </figcaption>
                </figure>
              )}

              {/*
                ★★ WAVE 9 — Annex A 37/38's approval COMMENT, in its own block and
                deliberately NOT merged with the reason above.

                ⚠ The two are different records and merging them would destroy the
                distinction the ledger's own constraint protects: `reason` is required
                for exactly REJECT and REVERSED and is what the PROVIDER is told; a
                comment is a remark for whoever reads the history, on any transition.
                A reader who could not tell which they were looking at would not know
                whether the provider had seen it.
              */}
              {typeof entry.officerComment === 'string' && entry.officerComment.length > 0 && (
                <figure className="pt-well p-3 mt-3 m-0">
                  <blockquote className="text-sm m-0">{entry.officerComment}</blockquote>
                  <figcaption className="text-xs mt-2 text-[var(--sgds-color-subtle)]">
                    Comment left by the deciding officer. Recorded against this decision and visible to anyone who
                    may read this bill&rsquo;s history, <strong>not</strong> sent to the healthcare service
                    provider.
                  </figcaption>
                </figure>
              )}

              {isReasonMissing(entry) && (
                <p role="alert" className="text-sm mt-3 text-[var(--sgds-danger-color-emphasis)]">
                  This rejection carries no reason. The database forbids that combination, so the record itself
                  looks wrong: report the bill reference to the service desk rather than treating this as an
                  ordinary rejection.
                </p>
              )}
            </li>
          ))}
        </ol>
        </>
      )}
    </div>
  )
}
